import { useEffect, useMemo, useState } from "react";
import type { Block } from "@models/ISchema";
import {
  blocksForTime,
  DAY_BY_ID,
  dayIdForDate,
  normalizeTime,
  SessionAssignationError,
} from "@services/collections/session-assignation";
import type {
  SessionAssignationRowPatch,
  SessionAssignationScheduleMode,
} from "@services/types/session-assignation";
import type { SessionAssignationEditorTarget } from "../../types/session-assignation";

export const useSessionTimeDraft = (
  target: SessionAssignationEditorTarget | null,
  blocks: readonly Block[],
  onDirty: (dirty: boolean) => void,
  isOpen = true,
) => {
  const initial = target?.values;
  const initialDraft = useMemo(
    () => ({
      target,
      values: {
        scheduleMode: target?.values.scheduleMode ?? null,
        startingTime: target?.values.startingTime ?? null,
        endingTime: target?.values.endingTime ?? null,
        blocksCountInput: target?.values.blocksCountInput ?? "",
      },
      issue: null as string | null,
    }),
    [target],
  );
  const [draft, setDraft] = useState(initialDraft);
  const { values, issue } = draft.target === target ? draft : initialDraft;
  useEffect(() => setDraft(initialDraft), [initialDraft]);
  const setIssue = (nextIssue: string | null) =>
    setDraft(current => ({
      ...(current.target === target ? current : initialDraft),
      issue: nextIssue,
    }));
  const catalog = useMemo(
    () =>
      blocks.filter(block => !initial?.date || block.day === DAY_BY_ID[dayIdForDate(initial.date)]),
    [blocks, initial?.date],
  );
  const startOptions = [...new Set(catalog.map(block => normalizeTime(block.startingTime)))]
    .sort()
    .map(value => ({ value, label: value.slice(0, 5) }));
  const endOptions = [...new Set(catalog.map(block => normalizeTime(block.endingTime)))]
    .sort()
    .map(value => ({ value, label: value.slice(0, 5) }));
  const dirty = !!(
    isOpen &&
    initial &&
    Object.keys(values).some(key => values[key] !== initial[key])
  );
  useEffect(() => {
    onDirty(dirty);
  }, [dirty, onDirty]);

  const change = (patch: Partial<typeof values>) => {
    setDraft(current => ({
      target,
      values: { ...(current.target === target ? current.values : initialDraft.values), ...patch },
      issue: null,
    }));
  };
  const setMode = (scheduleMode: SessionAssignationScheduleMode) => {
    if (scheduleMode === "unscheduled")
      change({ scheduleMode, startingTime: null, endingTime: null });
    else
      change({
        scheduleMode,
        startingTime: values.startingTime ?? startOptions[0]?.value ?? null,
        endingTime: values.endingTime ?? endOptions[0]?.value ?? null,
      });
  };

  const patch = (): SessionAssignationRowPatch | null => {
    if (!target || !isOpen) return null;
    if (!values.scheduleMode) {
      setIssue("SCHEDULE_MODE_REQUIRED");
      return null;
    }
    try {
      if (values.scheduleMode === "scheduled") {
        if (!values.startingTime || !values.endingTime)
          throw new SessionAssignationError("INVALID_TIME_RANGE");
        if (initial.date)
          blocksForTime(initial.date, values.startingTime, values.endingTime, blocks);
      }
      return {
        ...values,
        scheduleMode: values.scheduleMode,
      };
    } catch (error) {
      setIssue(error instanceof SessionAssignationError ? error.code : "INVALID_TIME_RANGE");
      return null;
    }
  };
  return { values, change, setMode, startOptions, endOptions, issue, setIssue, patch };
};
